import { IonCard, IonCardContent, IonContent, IonIcon, IonPage, IonRouterLink, IonText } from '@ionic/react';
import React from 'react';
import { type RouteComponentProps } from 'react-router';

import { warningOutline } from 'ionicons/icons';
import { useGetAirQualityMarkers } from '../../api/queries/getAirQualityMarkers';
import { useGetSingleNetwork } from '../../api/queries/getSingleNetworks';
import { AqiCard } from '../../components/AqiCard';
import { useSettings } from '../../contexts/SettingsContext';
import { type DatasourceId, type NetworkId } from '../../types';
import { css } from '@emotion/react';
import { createMapUrl } from '../../shared/url-utils';

const CurrentAQWidget: React.FC<
  RouteComponentProps<{
    networkId: NetworkId;
  }>
> = ({
  match: {
    params: { networkId },
  },
}) => {
  const queryParams = new URLSearchParams(window.location.search);
  const datasourceId =
    (queryParams.get('ds') as DatasourceId | undefined) ??
    (queryParams.get('datasourceId') as DatasourceId | undefined);

  // Note: we use isFetching here so that we can react to
  // background refetches and not just the initial one.
  const { data: networkData, isFetching: isLoadingNetwork } = useGetSingleNetwork({ networkId });
  const { setNetworkInfo, setMarker, marker, network, mapSettings, setMapSettings } = useSettings();
  const { data: markersData, isFetching: isLoadingMarkers } = useGetAirQualityMarkers({
    network: networkId,
  });
  const [isMarkerMissing, setIsMarkerMissing] = React.useState(false);

  React.useEffect(() => {
    if (!isLoadingNetwork && networkData) {
      const { network, aqiStandard } = networkData.data;
      setNetworkInfo(network, aqiStandard);
    }
  }, [networkData, isLoadingNetwork]);

  React.useEffect(() => {
    if (!isLoadingMarkers && markersData) {
      const marker = markersData.data.markers.find((marker) => marker.datasourceId === datasourceId);
      setMapSettings(markersData.data.mapSettings);
      if (!marker) {
        setIsMarkerMissing(true);
        return;
      }
      setIsMarkerMissing(false);
      setMarker(marker);
    }
  }, [
    markersData,
    isLoadingMarkers,
    datasourceId,
    networkData /* networkData is a dependency so that we can set the marker and datasource after a network fetch */,
  ]);

  const goToMapLink = React.useMemo(
    () =>
      datasourceId &&
      (marker ?? undefined) &&
      (network ?? undefined) &&
      (mapSettings ?? undefined) &&
      createMapUrl(networkId, datasourceId, {
        longitude: marker!.location.coordinates[0],
        latitude: marker!.location.coordinates[1],
        zoom: mapSettings!.zoom * 1.3, // zoom in a bit
      }),
    [networkId, marker, network, mapSettings]
  );

  return (
    <IonPage>
      <IonContent>
        {!isMarkerMissing ? (
          <IonRouterLink href={goToMapLink} target="_blank" rel="noopener noreferrer">
            <AqiCard showDatasourceName showPoweredBy />
          </IonRouterLink>
        ) : (
          <IonCard color="warning">
            <IonCardContent css={css({ display: 'flex', alignItems: 'center' })}>
              <IonIcon color="danger" size="small" icon={warningOutline} css={css({ marginRight: '.5rem' })} />
              <IonText>Data not available.</IonText>
            </IonCardContent>
          </IonCard>
        )}
      </IonContent>
    </IonPage>
  );
};

export default CurrentAQWidget;
